/**
 * The "what is it doing right now" indicator.
 *
 * A dead spinner tells a non-technical user nothing and makes ten seconds feel
 * like a minute. The server emits a coarse `status` event per phase
 * (`SEARCHING_KNOWLEDGE`, `READING_TICKETS`, …) and this renders it as a line
 * of plain language.
 *
 * ACCESSIBILITY: this element — not the transcript — is the polite live region
 * for a turn in progress. There are at most five state changes per answer, so
 * announcing them is genuinely useful, whereas announcing each streamed token
 * would make the page unusable with a screen reader.
 */

import { Spinner, Typography } from "@norican/react";

import { useT } from "@/i18n";

import type { AssistantStatusEvent, AssistantStreamState } from "./contract";
import { useLocalizedText } from "./localized";

/** The five states the contract defines; anything else falls back to the server text. */
const KNOWN_STATES: readonly AssistantStreamState[] = [
  "THINKING",
  "SEARCHING_KNOWLEDGE",
  "READING_TICKETS",
  "PREPARING_ACTION",
  "WRITING",
];

/**
 * Tools that get their own line instead of their phase's generic one. A
 * request takes three or four of them in a row (find the offering, read its
 * form, fill it in), and "Preparing an action…" for fifteen seconds reads as
 * stuck; naming each step shows it is getting somewhere.
 */
const TOOL_LABELS = {
  find_request_offering: "status.tool.find_request_offering",
  get_request_offering_form: "status.tool.get_request_offering_form",
  present_request_form: "status.tool.present_request_form",
  present_incident_form: "status.tool.present_incident_form",
  create_ticket: "status.tool.create_ticket",
  submit_service_request: "status.tool.submit_service_request",
  find_employee: "status.tool.find_employee",
} as const;

export interface StreamStatusProps {
  /** The latest `status` event, or null before the first one arrives. */
  activity: AssistantStatusEvent | null;
  className?: string;
}

export function StreamStatus({ activity, className }: StreamStatusProps) {
  const { t } = useT("assistant");
  const localize = useLocalizedText();

  const tool = activity?.detail?.params?.tool;
  const toolKey =
    tool !== undefined && Object.hasOwn(TOOL_LABELS, tool)
      ? TOOL_LABELS[tool as keyof typeof TOOL_LABELS]
      : null;
  const label: string =
    activity === null
      ? t("status.THINKING")
      : toolKey !== null
        ? t(toolKey)
        : (KNOWN_STATES as readonly string[]).includes(activity.state)
          ? t(`status.${activity.state}`)
          : // A state added after this build shipped: show the server's own
            // sentence rather than dropping the indicator entirely.
            localize(activity.detail) || t("status.THINKING");

  return (
    <div className={className} role="status" aria-live="polite" aria-atomic="true">
      <div className="flex items-center gap-2">
        <Spinner size="sm" tone="accent" label={label} />
        <Typography variant="body-sm" tone="muted">
          {label}
        </Typography>
      </div>
    </div>
  );
}
